import "./styles.scss";
import React, { useContext, useEffect } from "react";
import { PathValue, useFieldArray, ArrayPath } from "react-hook-form";
import { Button, Icon, Image } from "semantic-ui-react";
import { Upload } from "antd";
import ImgCrop from "antd-img-crop";
import { Svg } from "../../../../../../assets";
import { PlayerCardWidgetOwner, Media } from "../../../../../../services/bedrock";
import { ProjectFileUploader } from "../../../../../../utils/projectFileUploader";
import _ from "lodash";
import { PlayerCardFieldBase } from "./PlayerCardFieldBase";
import { PlayerCardSettingsContext } from "../../../../Contexts";

type PlayerCardImageFieldProps<TForm extends Record<string, any> = PlayerCardWidgetOwner> = {
  index: number;
  fieldName: ValidMediaArrayPath<TForm>;
  fieldLabel: string;
  cropShape?: "round" | "rect";
  aspect?: number;
  disabled?: boolean;
  imageUrl?: string | null;
  allowMultiple?: boolean;
};

type ValidMediaArrayPath<TForm> = {
  [K in ArrayPath<TForm>]: PathValue<TForm, K> extends Media[] ? K : never;
}[ArrayPath<TForm>];

const PlayerCardImageField: React.FC<PlayerCardImageFieldProps> = ({
  index,
  fieldName,
  fieldLabel,
  cropShape = "rect",
  aspect = 1 / 1,
  disabled = false,
  imageUrl,
  allowMultiple = false,
}) => {
  const {
    control,
    getValues,
    isTemplate,
    templateValue,
    getTemplateLockButton,
    getLockedTemplateAlert,
    useDefaultValueButton,
  } = PlayerCardFieldBase({ index, fieldName, fieldLabel });
  const { User } = useContext(PlayerCardSettingsContext);

  const {
    fields: mediaFields,
    replace: replaceMedia,
    remove: removeMedia,
  } = useFieldArray({
    control,
    name: `widgets.${index}.owner.${fieldName}`,
    keyName: "formId",
  });

  const uploader = new ProjectFileUploader("playerCard");

  const handleImageChange = async (file: File, index: number): Promise<any> => {
    const timestamp = new Date().getTime();
    const fileName = file.name.split(".")[0] + timestamp;
    const mediaCategory = file.type.includes("image/") ? "IMAGE" : "VIDEO";
    const media: Media = {
      id: -Date.now(),
      sortOrder: 0,
      category: mediaCategory,
      uri: URL.createObjectURL(file),
      type: file.type,
      extension: file.type.split("/")[1],
      imageFile: file,
      fileName,
    };
    const newFields: Media[] = [...mediaFields];
    newFields[index] = media;
    replaceMedia(newFields);

    const uploaded = await uploader.prepareMedia([media], () => {});
    newFields[index] = uploaded[0];
    replaceMedia(newFields);
  };

  useEffect(() => {
    if (imageUrl === undefined) return;
    if (imageUrl === null) {
      replaceMedia([]);
    } else {
      const media: Media = {
        id: -Date.now(),
        sortOrder: 0,
        category: "IMAGE",
        uri: imageUrl,
        type: "image/jpeg",
        extension: "jpg",
      };
      uploader.prepareMedia([media], () => {}).then(uploaded => replaceMedia(uploaded));
    }
  }, [imageUrl]);

  const showAddImage = allowMultiple || mediaFields.length === 0;

  const getImgCrop = (imgIndex: number, child: JSX.Element) => {
    return (
      <ImgCrop
        cropShape={cropShape}
        aspect={aspect}
        showGrid
        fillColor="transparent"
        minZoom={0.2}
        modalClassName="playerCardImageField-cropModal"
        cropperProps={{
          restrictPosition: false,
          zoomSpeed: 1,
          style: {},
          mediaProps: {},
        }}
        onModalOk={e => handleImageChange(e as File, imgIndex)}
      >
        <Upload action={() => Svg.UserBorder} showUploadList={false} listType="picture" disabled={disabled}>
          {child}
        </Upload>
      </ImgCrop>
    );
  };

  var templateValueMedia: Media[] = templateValue;
  var isNotSetToDefault = false;
  const hasDefault = templateValue !== undefined && templateValue !== null && templateValue !== "";

  if (!isTemplate && hasDefault) {
    if (!Array.isArray(templateValueMedia)) templateValueMedia = [];
    var templateUris = templateValueMedia.map(media => media.uri);
    var fieldUris = mediaFields.map(media => media.uri);
    isNotSetToDefault = !_.isEqual(fieldUris, templateUris);
  }

  const useDefaultValue = () => {
    const media: Media[] = templateValueMedia.map((media, index) => ({ ...media, id: -Date.now(), sortOrder: index }));
    replaceMedia(media);
  };

  return (
    <div className={`playerCardField playerCardImageField ${!User.hasWritePermission ? "readOnly" : ""}`}>
      <div className="playerCardFieldLabel">
        {getTemplateLockButton()}
        {fieldLabel}
        {isNotSetToDefault && getLockedTemplateAlert()}
      </div>

      <div className="imageInputs">
        {mediaFields.map((field, index) => {
          if (!allowMultiple && index > 0) return null;
          return (
            <div key={`image-field-${index}`} className="imageContainer">
              {getImgCrop(
                index,
                <Image
                  style={{ width: 100 * aspect, borderRadius: cropShape === "round" ? "50%" : 5 }}
                  src={field.uri}
                />
              )}
              {User.hasWritePermission && (
                <Button
                  circular
                  icon="trash alternate outline"
                  onClick={() => removeMedia(index)}
                  className="deleteImage"
                />
              )}
            </div>
          );
        })}
        {showAddImage &&
          getImgCrop(
            mediaFields.length,
            <div className="uploadImage" title="Upload image">
              <Icon name="add circle" className="addImage" size="huge" />
            </div>
          )}
      </div>
      {isNotSetToDefault && useDefaultValueButton(useDefaultValue)}
    </div>
  );
};
export default PlayerCardImageField;
